import {
  Badge,
  Button,
  Card,
  Input,
  mergeClasses,
  Title2,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Copy16Filled,
  Copy16Regular,
  Search24Filled,
  Search24Regular,
  ChevronDown16Filled,
  ChevronDown16Regular,
  ChevronRight16Filled,
  ChevronRight16Regular,
} from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';

import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard/useCopyToClipboard';
import type { ParsedVariant } from '../../../types/debug';

import type { VariantsPanelProps } from './VariantsPanel.interface';
import { useStyles } from './VariantsPanel.style';

const SearchIcon = bundleIcon(Search24Filled, Search24Regular) as React.FC;
const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;
const ExpandIcon = bundleIcon(
  ChevronDown16Filled,
  ChevronDown16Regular,
) as React.FC;
const CollapseIcon = bundleIcon(
  ChevronRight16Filled,
  ChevronRight16Regular,
) as React.FC;

function FlagValueDisplay({ value }: { value: string }) {
  const styles = useStyles();
  if (value === 'true') {
    return (
      <span className={mergeClasses(styles.flagValue, styles.flagTrue)}>
        true
      </span>
    );
  }
  if (value === 'false') {
    return (
      <span className={mergeClasses(styles.flagValue, styles.flagFalse)}>
        false
      </span>
    );
  }
  return (
    <span className={mergeClasses(styles.flagValue, styles.flagOther)}>
      {value}
    </span>
  );
}

function HighlightText({ text }: { text: string; search: string }) {
  return <>{text}</>;
}

function FlagRow({
  variant,
  search,
}: {
  variant: ParsedVariant;
  search: string;
}) {
  const styles = useStyles();
  const { copied, copy } = useCopyToClipboard();

  return (
    <div className={styles.flagRow}>
      <Tooltip content={variant.key} relationship="description">
        <span className={styles.flagKey}>
          <HighlightText text={variant.key} search={search} />
        </span>
      </Tooltip>
      <FlagValueDisplay value={variant.value} />
      <Tooltip content={copied ? 'Copied!' : 'Copy value'} relationship="label">
        <Button
          className={styles.copyBtn}
          appearance="subtle"
          size="small"
          icon={<CopyIcon />}
          onClick={() => copy(variant.value)}
          aria-label={`Copy value of ${variant.key} to clipboard`}
        />
      </Tooltip>
    </div>
  );
}

export function VariantsPanel({ data }: VariantsPanelProps) {
  const styles = useStyles();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [augLoopExpanded, setAugLoopExpanded] = useState(true);
  const [otherExpanded, setOtherExpanded] = useState(true);

  // Debounce search input by 150ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 150);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filtered = useMemo(() => {
    if (!debouncedSearch) return data.variants;
    const lower = debouncedSearch.toLowerCase();
    return data.variants.filter(
      (v) =>
        v.key.toLowerCase().includes(lower) ||
        v.value.toLowerCase().includes(lower),
    );
  }, [data.variants, debouncedSearch]);

  const augLoopFlags = useMemo(
    () => filtered.filter((v) => v.isAugLoopFlag),
    [filtered],
  );
  const otherFlags = useMemo(
    () => filtered.filter((v) => !v.isAugLoopFlag),
    [filtered],
  );

  if (data.variants.length === 0) {
    return (
      <Card className={styles.root}>
        <Title2 className={styles.header}>Feature Flags</Title2>
        <div className={styles.emptyState}>No feature flags found.</div>
      </Card>
    );
  }

  return (
    <Card className={styles.root}>
      <div className={styles.headerRow}>
        <Title2 className={styles.header}>Feature Flags</Title2>
        <Input
          className={styles.searchInput}
          placeholder="Search flags..."
          contentBefore={<SearchIcon />}
          value={searchInput}
          onChange={(_e, d) => setSearchInput(d.value)}
          aria-label="Search feature flags"
        />
      </div>

      {debouncedSearch && (
        <span className={styles.matchCount}>
          Showing {filtered.length.toString()} of{' '}
          {data.variants.length.toString()} flags
          {debouncedSearch ? ` matching "${debouncedSearch}"` : ''}
        </span>
      )}

      {/* AugLoop Flags */}
      {augLoopFlags.length > 0 && (
        <>
          <button
            className={styles.groupHeader}
            onClick={() => setAugLoopExpanded((e) => !e)}
            aria-expanded={augLoopExpanded}
            type="button"
          >
            {augLoopExpanded ? <ExpandIcon /> : <CollapseIcon />}
            <span className={styles.groupTitle}>Agent Flags</span>
            <Badge appearance="tint" color="brand" size="small">
              {augLoopFlags.length.toString()}
            </Badge>
          </button>
          {augLoopExpanded && (
            <div>
              {augLoopFlags.map((v) => (
                <FlagRow key={v.raw} variant={v} search={debouncedSearch} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Other Flags */}
      {otherFlags.length > 0 && (
        <>
          <button
            className={styles.groupHeader}
            onClick={() => setOtherExpanded((e) => !e)}
            aria-expanded={otherExpanded}
            type="button"
          >
            {otherExpanded ? <ExpandIcon /> : <CollapseIcon />}
            <span className={styles.groupTitle}>Other Flags</span>
            <Badge appearance="tint" color="informative" size="small">
              {otherFlags.length.toString()}
            </Badge>
          </button>
          {otherExpanded && (
            <div>
              {otherFlags.map((v) => (
                <FlagRow key={v.raw} variant={v} search={debouncedSearch} />
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
